import { Cancel, NavigateNext } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import {
  Button,
  CircularProgress,
  Collapse,
  Grid,
  Switch,
  TextField,
} from '@mui/material';
import { ValidationStringLengthEnum } from 'domain/enums/ValidationStringLengthEnum';
import { UnpyEvent } from 'domain/event/UnpyEvent';
import { UnpyEventView } from 'domain/event/UnpyEventView';
import { InputWrapper } from 'primary/Components/Input/InputWrapper';
import { StructureSelector } from 'primary/Components/Ping/StructureSelector';
import { useContextDependency } from 'primary/hooks/useContextDependency';
import { useTranslate, UseTranslateReturn } from 'primary/hooks/useTranslate';
import { useYupValidationResolver } from 'primary/hooks/useYupValidationResolver';
import { setTeamCreating } from 'primary/teams/store/TeamCreatingSlice';
import React from 'react';
import { useController, useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { RegisterTypeEnum } from 'secondary/event/RegisterTypeEnum';
import * as yup from 'yup';

export type FormStepNameData = {
  name?: string;
  participationAsStructure?: boolean;
  structure?: string;
  code?: string;
};

const validationSchema = (t: UseTranslateReturn) =>
  yup.object({
    name: yup
      .string()
      .max(
        ValidationStringLengthEnum.DEFAULT,
        t('general.forms.errors.tooLong', {
          maxChar: ValidationStringLengthEnum.DEFAULT,
        }),
      )
      .required(t('general.forms.errors.required')),
    structure: yup.string().when('participationAsStructure', {
      is: (val?: boolean) => !!val,
      then: yup
        .string()
        .max(
          ValidationStringLengthEnum.SMALL,
          t('general.forms.errors.tooLong', {
            maxChar: ValidationStringLengthEnum.SMALL,
          }),
        )
        .required(t('general.forms.errors.required')),
      otherwise: yup.string().nullable(),
    }),
  });

const validationSchemaWithCodeEvent = (t: UseTranslateReturn) =>
  yup.object({
    code: yup
      .string()
      .max(
        ValidationStringLengthEnum.DEFAULT,
        t('general.forms.errors.tooLong', {
          maxChar: ValidationStringLengthEnum.DEFAULT,
        }),
      )
      .required(t('general.forms.errors.required')),
    name: yup
      .string()
      .max(
        ValidationStringLengthEnum.DEFAULT,
        t('general.forms.errors.tooLong', {
          maxChar: ValidationStringLengthEnum.DEFAULT,
        }),
      )
      .required(t('general.forms.errors.required')),
    structure: yup.string().when('participationAsStructure', {
      is: (val?: boolean) => !!val,
      then: yup
        .string()
        .max(
          ValidationStringLengthEnum.SMALL,
          t('general.forms.errors.tooLong', {
            maxChar: ValidationStringLengthEnum.SMALL,
          }),
        )
        .required(t('general.forms.errors.required')),
      otherwise: yup.string().nullable(),
    }),
  });

type FormStepNameProps = {
  handleClose: () => void;
  nextStep: (data: FormStepNameData) => void;
  previousStep: (data: FormStepNameData) => void;
  initialValues: FormStepNameData;
  event: UnpyEvent | UnpyEventView;
};
export const FormStepName = ({
  nextStep,
  previousStep,
  initialValues,
  event,
  handleClose,
}: FormStepNameProps) => {
  const t = useTranslate();
  const { codeEventRepository } = useContextDependency();
  const [errorCode, setErrorCode] = React.useState<boolean>(false);
  const [isCodeLoading, setIsCodeLoading] = React.useState<boolean>(false);
  const resolver = useYupValidationResolver(
    event.registerType === RegisterTypeEnum.CODE
      ? validationSchemaWithCodeEvent(t)
      : validationSchema(t),
  );
  const dispatch = useDispatch();
  const enhancedOnSubmit = async (data: FormStepNameData) => {
    if (event.registerType === RegisterTypeEnum.CODE) {
      setIsCodeLoading(true);
      setErrorCode(false);
      if (!data.code) return;
      const { isConsummable: isCodeValid } = await codeEventRepository.checkValidity(
        data.code,
        event.id,
      );
      if (!isCodeValid) {
        setErrorCode(true);
        setIsCodeLoading(false);
        return Promise.reject();
      } else {
        return nextStep(data);
      }
    } else {
      return nextStep(data);
    }
  };

  const {
    handleSubmit,
    control,
    setValue,
    trigger,
    register,
    getValues,
    watch,
    formState: { isSubmitting, errors },
  } = useForm<FormStepNameData>({
    resolver,
    defaultValues: initialValues,
  });
  const { authRepository } = useContextDependency();

  const structureControl = useController({
    control: control,
    name: 'structure',
  });

  const isParticipationAsStructure = watch('participationAsStructure');
  return (
    <div className={'formStepName'}>
      <form onSubmit={handleSubmit(enhancedOnSubmit)}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <InputWrapper
              className={'eventAppearenceForm__field'}
              label={t('event.modal.nameLabel')}
              errors={errors}
              isSubmitting={isSubmitting}
              required
              Input={TextField}
              inputProps={{
                placeholder: t('event.modal.namePlaceholder'),
                disabled: isSubmitting,
                ...register(`name`),
                variant: 'filled',
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            {event.registerType === RegisterTypeEnum.CODE && (
              <InputWrapper
                className={'eventAppearenceForm__field'}
                label={t('event.modal.codeLabel')}
                errors={errors}
                isSubmitting={isSubmitting}
                Input={TextField}
                required
                inputProps={{
                  placeholder: t('event.modal.codePlaceholder'),
                  disabled: isSubmitting,
                  ...register(`code`),
                  variant: 'filled',
                }}
              />
            )}

            <div className={'-codeCheckContainer'}>
              {isCodeLoading && (
                <>
                  <CircularProgress size={'small'} color={'info'} />
                  <span>vérification du code</span>
                </>
              )}
              {!isCodeLoading && errorCode && (
                <span className={'-errorCode'}>Code invalide</span>
              )}
            </div>
          </Grid>
          <Grid item xs={12} sm={6}>
            <InputWrapper
              label={t('event.modal.selectStructureHelper')}
              errors={errors}
              className={'unpy-form__field'}
              isSubmitting={isSubmitting}
              Input={Switch}
              noFullWidth
              inputProps={{
                ...register('participationAsStructure', { required: true }),
                defaultChecked: !!getValues('participationAsStructure'),
              }}
            />
            <Collapse in={isParticipationAsStructure && !!authRepository.currentUser?.id}>
              <StructureSelector
                userId={authRepository?.currentUser?.id as number}
                errors={errors}
                control={structureControl}
              />
            </Collapse>
          </Grid>
          <Grid item xs={12}>
            <Button
              variant={'outlined'}
              color={'primary'}
              onClick={() => {
                dispatch(setTeamCreating(undefined));
                handleClose?.();
              }}
              className={'cancel'}
            >
              <Cancel />
              {t('event.register.cancelCreatingTeam')}
            </Button>
            <LoadingButton
              loading={isCodeLoading}
              variant={'contained'}
              color={'primary'}
              type={'submit'}
            >
              <NavigateNext />
              {t('formStepper.nextLabel')}
            </LoadingButton>
          </Grid>
        </Grid>
      </form>
    </div>
  );
};
